import { Injectable } from '@angular/core';

import { Casing } from '../../models/filename-conventions/casing';
import {
  FilenameConventionEvBased,
  FilenameConventionRangeBased,
} from '../../models/filename-conventions/filename-convention-payload';
import { TokenFormat } from '../../models/filename-conventions/token-format';

export interface RenderError {
  code: string;
  description: string;
}

export interface RenderResult {
  value: string | null;
  error: RenderError | null;
}

const TOKEN_PATTERN = /^\{([^{}]+)\}$/;
const CUSTOM_SLUG = 'custom';
const SEPARATOR_CHARS: readonly string[] = ['_', '-', '.'];

interface Piece {
  text: string;
  isTokenSlot: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FilenameConventionPreviewService {
  renderRangeBased(
    payload: FilenameConventionRangeBased,
    selectIndex: number,
    tokenValues: Record<string, string>,
  ): RenderResult {
    const entry = payload.entries.find((candidate) => {
      const offsetIndex = selectIndex - candidate.indexOffset;
      const aboveFrom = offsetIndex >= candidate.from;
      const belowTo = candidate.to === undefined || candidate.to === null || offsetIndex <= candidate.to;
      return aboveFrom && belowTo;
    });

    if (!entry) {
      return {
        value: null,
        error: {
          code: 'FilenameConvention.RangeEntryNotFound',
          description: `No range entry covers select index ${selectIndex}.`,
        },
      };
    }

    const pieces = this.buildPieces(entry.tokenFormats, tokenValues);
    const rendered = this.collapseAdjacentLiteralsAroundEmptyTokens(pieces);
    return { value: this.applyCasing(rendered, entry.casing), error: null };
  }

  renderTokenForm(
    payload: FilenameConventionEvBased,
    masterSuiteId: string,
    masterSuiteEnhancementVariantId: string,
    masterSuiteEnhancementVariantAssetId: string,
  ): RenderResult {
    const perSelect = this.locatePerSelect(
      payload,
      masterSuiteId,
      masterSuiteEnhancementVariantId,
      masterSuiteEnhancementVariantAssetId,
    );
    if (perSelect.error) return perSelect;
    const pieces = this.buildPlaceholderPieces(perSelect.tokenFormats);
    return { value: this.collapseAdjacentLiteralsAroundEmptyPieces(pieces), error: null };
  }

  renderEvBased(
    payload: FilenameConventionEvBased,
    masterSuiteId: string,
    masterSuiteEnhancementVariantId: string,
    masterSuiteEnhancementVariantAssetId: string,
    tokenValues: Record<string, string>,
  ): RenderResult {
    const perSelect = this.locatePerSelect(
      payload,
      masterSuiteId,
      masterSuiteEnhancementVariantId,
      masterSuiteEnhancementVariantAssetId,
    );
    if (perSelect.error) return perSelect;
    const pieces = this.buildPieces(perSelect.tokenFormats, tokenValues);
    return { value: this.collapseAdjacentLiteralsAroundEmptyTokens(pieces), error: null };
  }

  private locatePerSelect(
    payload: FilenameConventionEvBased,
    masterSuiteId: string,
    masterSuiteEnhancementVariantId: string,
    masterSuiteEnhancementVariantAssetId: string,
  ):
    | { tokenFormats: readonly TokenFormat[]; error: null }
    | { tokenFormats: readonly TokenFormat[]; error: RenderError; value: null } {
    const entry = payload.entries.find(
      (candidate) =>
        candidate.masterSuiteId === masterSuiteId &&
        candidate.masterSuiteEnhancementVariantId === masterSuiteEnhancementVariantId,
    );

    if (!entry) {
      return {
        tokenFormats: [],
        value: null,
        error: {
          code: 'FilenameConvention.EvEntryNotFound',
          description: `No EV entry for master suite '${masterSuiteId}' / EV '${masterSuiteEnhancementVariantId}'.`,
        },
      };
    }

    const perSelect = entry.selects.find(
      (candidate) =>
        candidate.masterSuiteEnhancementVariantAssetId === masterSuiteEnhancementVariantAssetId,
    );

    if (!perSelect) {
      return {
        tokenFormats: [],
        value: null,
        error: {
          code: 'FilenameConvention.EvSelectNotFound',
          description: `No per-select entry for asset '${masterSuiteEnhancementVariantAssetId}'.`,
        },
      };
    }

    return { tokenFormats: perSelect.tokenFormats, error: null };
  }

  private buildPlaceholderPieces(tokenFormats: readonly TokenFormat[]): Piece[] {
    const ordered = [...tokenFormats].sort((a, b) => a.order - b.order);
    return ordered.map((tokenFormat) => {
      const match = TOKEN_PATTERN.exec(tokenFormat.value);
      if (match) {
        return { text: `{${match[1]}}`, isTokenSlot: true };
      }
      return { text: tokenFormat.value, isTokenSlot: false };
    });
  }

  private buildPieces(
    tokenFormats: readonly TokenFormat[],
    tokenValues: Record<string, string>,
  ): Piece[] {
    const ordered = [...tokenFormats].sort((a, b) => a.order - b.order);
    return ordered.map((tokenFormat) => {
      const match = TOKEN_PATTERN.exec(tokenFormat.value);
      if (match) {
        const slug = match[1];
        if (slug.toLowerCase() === CUSTOM_SLUG) {
          return { text: '', isTokenSlot: true };
        }
        const resolved = Object.prototype.hasOwnProperty.call(tokenValues, slug)
          ? tokenValues[slug]
          : '';
        return { text: resolved, isTokenSlot: true };
      }
      return { text: tokenFormat.value, isTokenSlot: false };
    });
  }

  private collapseAdjacentLiteralsAroundEmptyTokens(pieces: readonly Piece[]): string {
    return pieces.reduce<string>((buffer, piece, pieceIndex) => {
      if (!piece.isTokenSlot && piece.text.length > 0) {
        const priorTokenEmpty =
          pieceIndex > 0 &&
          pieces[pieceIndex - 1].isTokenSlot &&
          pieces[pieceIndex - 1].text.length === 0;
        const nextTokenEmpty =
          pieceIndex < pieces.length - 1 &&
          pieces[pieceIndex + 1].isTokenSlot &&
          pieces[pieceIndex + 1].text.length === 0;

        if (priorTokenEmpty) return buffer;
        if (nextTokenEmpty) return buffer;
      }
      return buffer + piece.text;
    }, '');
  }

  private collapseAdjacentLiteralsAroundEmptyPieces(pieces: readonly Piece[]): string {
    const nonEmpty = pieces.filter((piece) => piece.text.length > 0);
    const dedupedSeparators = nonEmpty.filter((piece, index) => {
      if (!this.isSeparatorPiece(piece)) return true;
      const nextPiece = nonEmpty[index + 1];
      if (nextPiece === undefined) return true;
      if (!this.isSeparatorPiece(nextPiece)) return true;
      return false;
    });
    return dedupedSeparators.map((piece) => piece.text).join('');
  }

  private isSeparatorPiece(piece: Piece): boolean {
    if (piece.isTokenSlot) return false;
    return SEPARATOR_CHARS.includes(piece.text);
  }

  private applyCasing(rendered: string, casing: Casing): string {
    if (casing === 'Upper') return rendered.toUpperCase();
    if (casing === 'Lower') return rendered.toLowerCase();
    return rendered;
  }
}
